import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', text: '#E8EDF5',
};

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }

    api.get(`/auth/verify-email?token=${token}`)
      .then(({ data }) => {
        if (data.verified || data.message?.includes('verified')) {
          setStatus('success');
          setMessage('Email verified! You can now login.');
        } else {
          setStatus('error');
          setMessage(data.error || 'Verification failed.');
        }
      })
      .catch((err) => {
        const data = err.response?.data;
        setStatus('error');
        setMessage(data?.error || 'Something went wrong. Please try again.');
        setResendEmail(data?.email || '');
      });
  }, [token]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResending(true);
    setResendMessage('');

    try {
      await api.post('/auth/resend-verification', { email: resendEmail });
      setResendMessage(`A new verification email has been sent to ${resendEmail}.`);
    } catch (err) {
      setResendMessage(err.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setResending(false);
    }
  };

  const navButtonStyle = {
    background: C.gold, color: C.ink, border: 'none',
    borderRadius: 10, padding: '12px 32px',
    fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%',
    fontFamily: 'inherit',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: C.ink,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Cabinet Grotesk', sans-serif",
      padding: 24,
    }}>
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 20,
        padding: 48,
        width: '100%',
        maxWidth: 400,
        textAlign: 'center',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      }}>
        <img
          src="/optika-logo.svg"
          alt="OptiKa"
          style={{ height: 36, width: 'auto', marginBottom: 8 }}
        />
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 32 }}>
          Email Verification
        </p>

        {status === 'verifying' && (
          <>
            <div style={{
              width: 48, height: 48,
              border: `4px solid ${C.border}`,
              borderTopColor: C.gold,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: C.muted }}>Verifying your email...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: 64, height: 64,
              background: 'rgba(0,201,177,0.12)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: 28,
              color: C.teal,
            }}>✓</div>
            <h2 style={{ color: C.teal, marginBottom: 8, fontSize: '1.3rem', fontWeight: 800 }}>Email Verified!</h2>
            <p style={{ color: C.muted, marginBottom: 24 }}>
              Your account is ready. Login to get started.
            </p>
            <button onClick={() => navigate('/login')} style={navButtonStyle}>
              Login to OptiKa
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: 64, height: 64,
              background: 'rgba(255,107,107,0.12)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: 28,
              color: C.coral,
            }}>✗</div>
            <h2 style={{ color: C.coral, marginBottom: 8, fontSize: '1.3rem', fontWeight: 800 }}>Verification Failed</h2>
            <p style={{ color: C.muted, marginBottom: 24 }}>{message}</p>
            {resendEmail && (
              <>
                <button
                  onClick={handleResend}
                  disabled={resending}
                  style={{
                    ...navButtonStyle,
                    marginBottom: 12,
                    cursor: resending ? 'not-allowed' : 'pointer',
                    opacity: resending ? 0.7 : 1,
                  }}
                >
                  {resending ? 'Sending...' : 'Send new verification email'}
                </button>
                {resendMessage && (
                  <p style={{ color: resendMessage.startsWith('A new') ? C.teal : C.coral, marginBottom: 16, fontSize: '0.85rem' }}>
                    {resendMessage}
                  </p>
                )}
              </>
            )}
            <button
              onClick={() => navigate('/login')}
              style={{
                background: 'transparent', color: C.text, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: '12px 32px',
                fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%',
                fontFamily: 'inherit',
              }}
            >
              Back to Login
            </button>
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
